import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useDomains } from './useDomains';
import { domainQueryKeys } from '../../domain/hooks/queryKeys';
import { domainApi } from '../../domain/api/domainApi';

// Type for subject data returned from API
interface SubjectData {
  id: string;
  name: string;
  code: string;
}

/**
 * Hook for fetching all subjects across all domains for a tenant
 * Returns a lookup map from subjectId to subject info with domain context
 */
export const useTenantAllSubjects = (tenantId: string) => {
  const { data, isLoading: domainsLoading, isError: domainsError } = useDomains(tenantId);

  // Flatten the infinite query pages to get all domains
  const domains = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.items.map((domain) => ({
        id: domain.domainId,
        tenantId: domain.tenantId,
        name: domain.name,
        code: domain.code,
        description: domain.description,
      }))
    );
  }, [data]);

  // Create queries for all domain subjects
  const subjectQueries = useQueries({
    queries: domains.map((domain) => ({
      queryKey: domainQueryKeys.subjectsByDomain(domain.id),
      queryFn: async () => {
        const response = await domainApi.subject.getSubjectsByDomain(domain.id);
        return response.data || [];
      },
      enabled: !!domain.id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Check loading and error states
  const isLoadingSubjects = subjectQueries.some((query) => query.isLoading);
  const hasSubjectError = subjectQueries.some((query) => query.isError);
  const isLoading = domainsLoading || isLoadingSubjects;
  const isError = domainsError || hasSubjectError;

  // Create subject lookup map
  const subjectLookup = useMemo(() => {
    if (!domains.length || subjectQueries.some((query) => !query.data)) {
      return new Map();
    }

    const lookup = new Map<
      string,
      {
        name: string;
        code: string;
        domainId: string;
        domainName: string;
        domainCode?: string;
      }
    >();

    domains.forEach((domain, index) => {
      const subjects = (subjectQueries[index]?.data as SubjectData[] | undefined) || [];
      subjects.forEach((subject) => {
        lookup.set(subject.id, {
          name: subject.name,
          code: subject.code,
          domainId: domain.id,
          domainName: domain.name,
          domainCode: domain.code,
        });
      });
    });

    return lookup;
  }, [domains, subjectQueries]);

  return {
    subjectLookup,
    isLoading,
    isError,
    domains,
  };
};
